/**
 * SAP 材料编码规则（2026-10-24）
 *
 * 依据用户提供的 SAP 编码规则表（面辅料 / 面料类型 / 业务类型 / 编码规则 / 示例数据）。
 * 11 个示例里 9 个能完整对上，2 个缺码表无法反推 —— 这些在 `SAP_CODE_PENDING` 里明示，
 * 生成器遇到缺字段时**拒绝生成而不是猜**。
 *
 * 已知规律：
 *   首字符是材料大类：1 = 原纱(YARN)、2 = 面料(FABRIC)、3 = 辅料(TRIM)
 *   位置一律按 1 开始计数（第 5 位 = index 4）
 *
 * 这是纯函数模块，前后端共用；实际落库时要先确认 SAP 字段与号段。
 */
/** 材料大类 */
export const MATERIAL_FAMILY = {
    YARN: '1',
    FABRIC: '2',
    TRIM: '3',
};
/** 业务场景 */
export const MATERIAL_SCENARIO = {
    /** 创建 P-BOM（Style 用）：大类 + 品牌 + 材料中分类 */
    STYLE_BOM: 'STYLE_BOM',
    /** 材料创建 / 库存材料订货（Stock 用）：大类 + 品牌 + 年度末位 + 季节 + 4 位流水 */
    STOCK_ORDER: 'STOCK_ORDER',
    /** 材料回收：年度取 style 物料对应年度的最后一位 */
    STOCK_RECYCLE: 'STOCK_RECYCLE',
    /** 出口：大类 + ET + 取位 + 4 位流水 */
    EXPORT: 'EXPORT',
    /** 辅料用于款式：物料组前 3 位 + 品牌 + 4 位流水 */
    TRIM_FOR_STYLE: 'TRIM_FOR_STYLE',
    /** 辅料上海分支采购：同上 + S */
    TRIM_SHANGHAI: 'TRIM_SHANGHAI',
    /** 辅料主数据管理 / 开发 / 出口：3 开头的物料组 + 品牌 + 4 位流水 */
    TRIM_DEV: 'TRIM_DEV',
};
const digits = (value, length) => {
    const text = (value ?? '').trim().toUpperCase();
    return text.length === length ? text : null;
};
const trimmed = (value) => {
    const text = (value ?? '').trim().toUpperCase();
    return text || null;
};
/** 出口场景：按参考码位数决定取哪几位（6 位取款号 5/6 位，9 位取原码 4/5 位） */
function exportTail(input, missing) {
    const source = trimmed(input.sourceCode);
    const style = trimmed(input.styleCode);
    if (!source && !style) {
        missing.push('参考码或款号（用于取位）');
        return [];
    }
    if (source && source.length === 9) {
        return [{ value: source.slice(3, 5), label: '原码第 4–5 位' }];
    }
    if (source && source.length === 6) {
        if (!style || style.length < 6) {
            missing.push('款号（需 6 位，取第 5、6 位）');
            return [];
        }
        return [{ value: `${style[4]}${style[5]}`, label: '款号第 5–6 位' }];
    }
    if (!source && style && style.length >= 6) {
        // 没有参考码、只有款号时，按 6 位规则处理
        return [{ value: `${style[4]}${style[5]}`, label: '款号第 5–6 位' }];
    }
    missing.push('参考码位数（只有 6 位或 9 位两种走法）');
    return [];
}
/**
 * 按规则生成 SAP 材料编码。
 * 缺关键字段时返回 `code: null` 并把缺项写进 `missing` —— 不做占位猜测。
 */
export function buildSapMaterialCode(input) {
    const segments = [];
    const missing = [];
    let confidence = 'certain';
    segments.push({ value: input.family, label: '材料大类' });
    const pushBrand = () => {
        const brand = digits(input.brandCd, 2);
        if (brand)
            segments.push({ value: brand, label: '品牌' });
        else
            missing.push('品牌（2 位）');
    };
    const pushMiddle = () => {
        const middle = digits(input.middleCategoryCd, 1);
        if (middle)
            segments.push({ value: middle, label: '材料中分类' });
        else
            missing.push('材料中分类（1 位）');
    };
    const pushYearSeasonSerial = (yearLabel) => {
        const year = digits(input.yearDigit, 1);
        if (year)
            segments.push({ value: year, label: yearLabel });
        else
            missing.push(`${yearLabel}（1 位）`);
        const season = digits(input.season, 1);
        if (season)
            segments.push({ value: season, label: '季节' });
        else
            missing.push('季节（1 位）');
        const serial = digits(input.serial, 4);
        if (serial)
            segments.push({ value: serial, label: '流水码' });
        else
            missing.push('流水码（4 位）');
    };
    switch (input.scenario) {
        case MATERIAL_SCENARIO.STYLE_BOM:
            pushBrand();
            pushMiddle();
            break;
        case MATERIAL_SCENARIO.STOCK_ORDER: {
            // 原纱另有「BU 用」走法：BU 代码后两位顶替品牌
            if (input.family === MATERIAL_FAMILY.YARN && input.buCode) {
                const bu = trimmed(input.buCode)?.slice(-2) ?? '';
                if (bu.length === 2)
                    segments.push({ value: bu, label: 'BU 代码后两位' });
                else
                    missing.push('BU 代码（至少 2 位）');
                confidence = 'assumed';
            }
            else {
                pushBrand();
            }
            pushYearSeasonSerial('年度末位');
            break;
        }
        case MATERIAL_SCENARIO.STOCK_RECYCLE:
            pushBrand();
            pushYearSeasonSerial('Style 物料对应年度末位');
            break;
        case MATERIAL_SCENARIO.EXPORT: {
            segments.push({ value: 'ET', label: '出口标记' });
            segments.push(...exportTail(input, missing));
            const serial = digits(input.serial, 4);
            if (serial)
                segments.push({ value: serial, label: '流水码' });
            else
                missing.push('流水码（4 位）');
            break;
        }
        case MATERIAL_SCENARIO.TRIM_FOR_STYLE:
        case MATERIAL_SCENARIO.TRIM_SHANGHAI:
        case MATERIAL_SCENARIO.TRIM_DEV: {
            // 辅料这三类：首段就是物料组前 3 位（本身以 3 开头），不再重复加品类位
            segments.shift();
            const group = trimmed(input.trimGroupPrefix) ?? (input.family === MATERIAL_FAMILY.TRIM ? null : null);
            if (group && /^3\d{2}$/.test(group))
                segments.push({ value: group, label: '物料组前 3 位' });
            else
                missing.push('物料组前 3 位（3 开头，如 301/314/321）');
            pushBrand();
            const serial = digits(input.serial, 4);
            if (serial)
                segments.push({ value: serial, label: '流水码' });
            else
                missing.push('流水码（4 位）');
            if (input.scenario === MATERIAL_SCENARIO.TRIM_SHANGHAI) {
                segments.push({ value: 'S', label: '上海分支标记' });
                confidence = 'certain';
            }
            break;
        }
        default:
            missing.push('业务场景');
    }
    return {
        code: missing.length ? null : segments.map((segment) => segment.value).join(''),
        segments,
        missing,
        confidence,
    };
}
export const SAP_CODE_RULES = [
    { family: MATERIAL_FAMILY.YARN, scenario: MATERIAL_SCENARIO.STYLE_BOM, rule: '1 + 品牌 + 材料中分类', sample: '1EED', verified: true },
    { family: MATERIAL_FAMILY.YARN, scenario: MATERIAL_SCENARIO.STOCK_ORDER, rule: '1 + 品牌 + 年度末位 + 季节 + 4 位流水（BU 用则取 BU 代码后两位）', sample: '1SM640003', verified: true },
    { family: MATERIAL_FAMILY.YARN, scenario: MATERIAL_SCENARIO.STOCK_RECYCLE, rule: '1 + 品牌 + style 物料对应年度末位 + 季节 + 4 位流水', sample: '1EE340016', verified: true },
    { family: MATERIAL_FAMILY.YARN, scenario: MATERIAL_SCENARIO.EXPORT, rule: '1 + ET + 款号第 5/6 位（6 位码）或原码第 4/5 位（9 位码）+ 4 位流水', sample: '1ETF20001', verified: true },
    { family: MATERIAL_FAMILY.FABRIC, scenario: MATERIAL_SCENARIO.STYLE_BOM, rule: '2 + 品牌 + 材料中分类', sample: '2EEK', verified: true },
    { family: MATERIAL_FAMILY.FABRIC, scenario: MATERIAL_SCENARIO.STOCK_ORDER, rule: '2 + 品牌 + 年度末位 + 季节 + 4 位流水', sample: '2P1640665', verified: false },
    { family: MATERIAL_FAMILY.FABRIC, scenario: MATERIAL_SCENARIO.STOCK_RECYCLE, rule: '2 + 品牌 + style 物料对应年度末位 + 季节 + 4 位流水', sample: '2EE760003', verified: true },
    { family: MATERIAL_FAMILY.FABRIC, scenario: MATERIAL_SCENARIO.EXPORT, rule: '2 + ET + 款号第 5/6 位（6 位码）或面料第 4/5 位（9 位码）+ 4 位流水', sample: '2ETH20034', verified: true },
    { family: MATERIAL_FAMILY.TRIM, scenario: MATERIAL_SCENARIO.TRIM_FOR_STYLE, rule: '物料组前 3 位 + 品牌 + 4 位流水', sample: '314NC0437', verified: true },
    { family: MATERIAL_FAMILY.TRIM, scenario: MATERIAL_SCENARIO.TRIM_SHANGHAI, rule: '物料组前 3 位 + 品牌 + 4 位流水 + S', sample: '321PR0157S', verified: true },
    { family: MATERIAL_FAMILY.TRIM, scenario: MATERIAL_SCENARIO.TRIM_DEV, rule: '3 开头的物料组 + 品牌 + 4 位流水', sample: '301EE0034', verified: true },
    { family: MATERIAL_FAMILY.TRIM, scenario: MATERIAL_SCENARIO.STOCK_ORDER, rule: '3 + 品牌 + 年度末位 + 季节 + 4 位流水', sample: null, verified: false },
];
export const SAP_CODE_QUESTIONS = [
    {
        id: 'brand',
        topic: '品牌代码',
        understanding: '取品牌管理页的代码（与款号前两位一致），如 EE / SM / NC / PR',
        status: 'confirmed',
        needsAnswer: false,
        ask: '品牌位取款号前两位，与我们品牌管理页的代码一致。请确认这种取法可行，'
            + '以及 SAP 侧是否存在与 PLM 品牌代码不一致的历史品牌（若有，需要一次性映射表）。',
    },
    {
        id: 'year',
        topic: '年度码',
        understanding: '取 SAP 年度，即款号第 5 位；2020 年起该位是字母（G=2026、H=2027）',
        status: 'confirmed',
        needsAnswer: false,
        ask: '我们按款号第 5 位取年度，因此 2026 年起的编码年度位会是字母（如 2NDH2…）。'
            + '规则表示例里都是数字，请在评审时确认字母年度位能被 SAP 接受。',
    },
    {
        id: 'season',
        topic: '季节码',
        understanding: '取款号第 6 位（1–8）',
        status: 'confirmed',
        needsAnswer: false,
        ask: '季节位取款号第 6 位。请确认与 27Q2 这类「年季」标签是 1:1 关系。',
    },
    {
        id: 'issuer',
        topic: '发号方式',
        understanding: 'PLM 侧已能按自有规则生成面料码；SAP 端手工录入不方便',
        status: 'partial',
        needsAnswer: true,
        ask: '能否由 PLM 创建面料主数据后，把生成的编码一并给到 SAP，SAP 侧直接采用、不再在 SAP 端录入发号？'
            + '如果可以，需要 SAP 给出号段分配与校验规则；如果不可以，请说明 SAP 侧的发号时机与回传方式。',
    },
    {
        id: 'bu',
        topic: 'BU 代码',
        understanding: '不清楚；规则表里只出现在「原纱—面料创建/库存订货（BU 用）」这一条',
        status: 'open',
        needsAnswer: true,
        ask: 'BU 代码清单是什么？什么情况下用 BU 走法而不是品牌走法？两者能否共存于同一批编码？',
    },
    {
        id: 'et',
        topic: 'ET 段',
        understanding: '示例都出现在出口场景，推测是固定出口标记',
        status: 'partial',
        needsAnswer: true,
        ask: 'ET 是否为固定出口标记？出口是否还有其他变体（不同贸易方式用不同字母）？',
    },
    {
        id: 'trimGroup',
        topic: '辅料物料组',
        understanding: '前 3 位、以 3 开头；辅料库已有部分代码（301 / 314 / 321）',
        status: 'partial',
        needsAnswer: true,
        ask: '请提供完整物料组清单（前 3 位）；是否所有辅料编码都以 3 开头？上海分支的 S 后缀是否只用于上海采购？',
    },
    {
        id: 'sample2P1',
        topic: '示例 2P1640665 的品牌段',
        understanding: '按「品牌 2 位」解出 P1，无法对应现有任何品牌代码',
        status: 'partial',
        needsAnswer: true,
        ask: '2P1640665 的品牌位是什么？是否为历史品牌代码或特殊业务类型？',
    },
];
/** 需要顾问回答的问题（界面用它生成"待确认"清单；只需确认的条目不计入） */
export const SAP_CODE_PENDING = SAP_CODE_QUESTIONS
    .filter((question) => question.needsAnswer)
    .map((question) => `${question.topic}：${question.ask}`);
/** 只需顾问确认"我们的做法可以"的条目 */
export const SAP_CODE_NOTICE = SAP_CODE_QUESTIONS
    .filter((question) => !question.needsAnswer)
    .map((question) => `${question.topic}：${question.ask}`);
export function styleCodeSignals(styleCd) {
    const code = (styleCd ?? '').trim().toUpperCase();
    if (!/^[A-Z0-9]{6,}$/.test(code))
        return null;
    return { brandCd: code.slice(0, 2), yearDigit: code[4], season: code[5] };
}
//# sourceMappingURL=material-code.js.map